import { CaregiverFormData } from './types';

export interface FieldErrors {
  [key: string]: string;
}

export function isValidPhone(value: string): boolean {
  return value.replace(/\D/g, '').length === 10;
}

export function isValidName(value: string): boolean {
  return /^[a-zA-Z\u00C0-\u00FF\s'-]*$/.test(value);
}

function isAtLeast18(dateStr: string): boolean {
  const dob = new Date(dateStr);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age >= 18;
}

function isInFluSeason(dateStr: string): boolean {
  const date = new Date(dateStr);
  const today = new Date();
  // Flu season start: previous June 30th
  const seasonStart = new Date(today.getFullYear(), 5, 30); // June = month 5
  if (today < seasonStart) {
    seasonStart.setFullYear(seasonStart.getFullYear() - 1);
  }
  return date >= seasonStart && date <= today;
}

function phoneFormatError(value: string): string | null {
  if (value && value.replace(/\D/g, '').length > 0 && !isValidPhone(value)) {
    return 'Must be 10 digits';
  }
  return null;
}

export function validateStep(step: number, formData: CaregiverFormData): FieldErrors {
  const errors: FieldErrors = {};

  switch (step) {
    case 1: {
      if (!formData.firstName.trim()) errors.firstName = 'Required';
      if (!formData.lastName.trim()) errors.lastName = 'Required';
      if (!formData.dateOfBirth) {
        errors.dateOfBirth = 'Required';
      } else if (!isAtLeast18(formData.dateOfBirth)) {
        errors.dateOfBirth = 'Must be at least 18 years old';
      }
      if (!formData.email.trim()) errors.email = 'Required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email';
      // Phone is optional but must be 10 digits if provided
      const phoneErr = phoneFormatError(formData.phone);
      if (phoneErr) errors.phone = phoneErr;
      if (!formData.gender) errors.gender = 'Required';
      if (!formData.countyName) errors.countyName = 'Required';
      if (!formData.primaryLanguage) errors.primaryLanguage = 'Required';
      if (!formData.howDidYouHearAboutUs) errors.howDidYouHearAboutUs = 'Required';
      if (!formData.street.trim()) errors.street = 'Required';
      if (!formData.city.trim()) errors.city = 'Required';
      if (!formData.state) errors.state = 'Required';
      if (!formData.zipCode.trim()) errors.zipCode = 'Required';
      else if (!/^\d{5}$/.test(formData.zipCode.trim())) errors.zipCode = 'Must be 5 digits';
      if (formData.physicalAddressDifferentThanMailing) {
        if (!formData.otherAddressLine1.trim()) errors.otherAddressLine1 = 'Required';
        if (!formData.otherCity.trim()) errors.otherCity = 'Required';
        if (!formData.otherState) errors.otherState = 'Required';
        if (!formData.otherZipCode.trim()) errors.otherZipCode = 'Required';
        else if (!/^\d{5}$/.test(formData.otherZipCode.trim())) errors.otherZipCode = 'Must be 5 digits';
      }
      break;
    }

    case 2: {
      if (!formData.ssn.trim()) errors.ssn = 'Required';
      else if (formData.ssn.replace(/\D/g, '').length !== 9) errors.ssn = 'Must be 9 digits';
      if (!formData.taxClassification) errors.taxClassification = 'Required';
      if (!formData.homecareExperienceYears) errors.homecareExperienceYears = 'Required';
      if (!formData.citizenStatus) errors.citizenStatus = 'Required';
      if (formData.citizenStatus === 'An alien authorized to work') {
        if (!formData.noncitizenDocumentType) errors.noncitizenDocumentType = 'Required';
        if (!formData.noncitizenExpirationDate) errors.noncitizenExpirationDate = 'Required';
        if (formData.noncitizenDocumentType === 'USCIS Number') {
          if (!formData.uscisNumber.trim()) errors.uscisNumber = 'Required';
          else if (formData.uscisNumber.replace(/\D/g, '').length > 9) errors.uscisNumber = 'Must be 9 digits or less';
        }
        if (formData.noncitizenDocumentType === 'Form I-94 Admission Number' && !formData.formI94AdmissionNumber.trim()) {
          errors.formI94AdmissionNumber = 'Required';
        }
        if (formData.noncitizenDocumentType === 'Foreign Passport') {
          if (!formData.foreignPassportNumber.trim()) errors.foreignPassportNumber = 'Required';
          if (!formData.foreignPassportCountry) errors.foreignPassportCountry = 'Required';
        }
      }
      if (formData.citizenStatus === 'A lawful permanent resident') {
        if (!formData.uscisNumber.trim()) errors.uscisNumber = 'Required';
        else if (formData.uscisNumber.replace(/\D/g, '').length > 9) errors.uscisNumber = 'Must be 9 digits or less';
      }
      if (formData.type === 'RN' || formData.type === 'LPN') {
        if (!formData.federalFilingStatus) errors.federalFilingStatus = 'Required';
      }
      break;
    }

    case 3: {
      if (!formData.emergencyContactName.trim()) errors.emergencyContactName = 'Required';
      if (!formData.emergencyContactRelationship) errors.emergencyContactRelationship = 'Required';
      if (!formData.emergencyContactPhone.trim()) errors.emergencyContactPhone = 'Required';
      else {
        const ecPhoneErr = phoneFormatError(formData.emergencyContactPhone);
        if (ecPhoneErr) errors.emergencyContactPhone = ecPhoneErr;
      }
      if (!formData.professionalReferenceName.trim()) errors.professionalReferenceName = 'Required';
      if (!formData.professionalReferenceRelationship) errors.professionalReferenceRelationship = 'Required';
      if (!formData.professionalReferencePhone.trim()) errors.professionalReferencePhone = 'Required';
      else {
        const prPhoneErr = phoneFormatError(formData.professionalReferencePhone);
        if (prPhoneErr) errors.professionalReferencePhone = prPhoneErr;
      }
      if (!formData.personalReferenceName.trim()) errors.personalReferenceName = 'Required';
      if (!formData.personalReferenceRelationship) errors.personalReferenceRelationship = 'Required';
      if (!formData.personalReferencePhone.trim()) errors.personalReferencePhone = 'Required';
      else {
        const perPhoneErr = phoneFormatError(formData.personalReferencePhone);
        if (perPhoneErr) errors.personalReferencePhone = perPhoneErr;
      }
      break;
    }

    case 4: {
      if (!formData.ableToDrive) errors.ableToDrive = 'Required';
      if (formData.ableToDrive === 'Yes' && !formData.hasACar) errors.hasACar = 'Required';
      // At least one county must be selected
      if (!formData.countyMiamiDade && !formData.countyBroward && !formData.countyPalmBeach) {
        errors.countySelection = 'At least one county must be selected';
      }
      // At least 2 areas per selected county
      if (formData.countyMiamiDade) {
        const areas = formData.areasOfWorkMiamiDade ? formData.areasOfWorkMiamiDade.split(';').filter(Boolean) : [];
        if (areas.length < 2) errors.areasOfWorkMiamiDade = 'Select at least 2 areas';
      }
      if (formData.countyBroward) {
        const areas = formData.areasOfWorkBroward ? formData.areasOfWorkBroward.split(';').filter(Boolean) : [];
        if (areas.length < 2) errors.areasOfWorkBroward = 'Select at least 2 areas';
      }
      if (formData.countyPalmBeach) {
        const areas = formData.areasOfWorkPalmBeach ? formData.areasOfWorkPalmBeach.split(';').filter(Boolean) : [];
        if (areas.length < 2) errors.areasOfWorkPalmBeach = 'Select at least 2 areas';
      }
      if (!formData.worksWithPets) errors.worksWithPets = 'Required';
      if (!formData.worksWithSmokers) errors.worksWithSmokers = 'Required';
      break;
    }

    case 5: {
      if (!formData.hasReceivedFluVaccineThisSeason) errors.hasReceivedFluVaccineThisSeason = 'Required';
      if (formData.hasReceivedFluVaccineThisSeason === 'Yes') {
        if (!formData.fluVaccineDate) {
          errors.fluVaccineDate = 'Required';
        } else if (!isInFluSeason(formData.fluVaccineDate)) {
          errors.fluVaccineDate = 'Must be within current flu season';
        }
      }
      if (formData.hasReceivedFluVaccineThisSeason === 'No') {
        if (!formData.fluVaccineDecision) errors.fluVaccineDecision = 'Required';
        if (formData.fluVaccineDecision === 'I disagree' && !formData.fluDeclinationReason) {
          errors.fluDeclinationReason = 'Required';
        }
        if (
          formData.fluDeclinationReason &&
          formData.fluDeclinationReason.includes('Other') &&
          !formData.fluDeclinationOtherReason
        ) {
          errors.fluDeclinationOtherReason = 'Required';
        }
      }
      if (!formData.sysHepBDecision) errors.sysHepBDecision = 'Required';
      if (formData.sysHepBDecision === 'I decline - I am already immune') {
        if (!formData.hepBImmuneStatus) errors.hepBImmuneStatus = 'Required';
        if (formData.hepBImmuneStatus === 'Vaccinated') {
          if (!formData.hepBInjection1) errors.hepBInjection1 = 'Required';
          if (!formData.hepBInjection2) errors.hepBInjection2 = 'Required';
          if (!formData.hepBInjection3) errors.hepBInjection3 = 'Required';
        }
      }
      if (!formData.tbSymptoms) errors.tbSymptoms = 'Required';
      if (!formData.exposedToTb) errors.exposedToTb = 'Required';
      break;
    }

    case 6: {
      if (!formData.portalSignature) {
        errors.portalSignature = 'Signature is required';
      } else if (formData.portalSignature.length < 250) {
        errors.portalSignature = 'Please provide a more complete signature';
      }
      break;
    }
  }

  return errors;
}
